import React, { useEffect, useRef, useState } from "react";
import { Snackbar, Alert, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  registerServiceWorker, applyUpdate, checkServiceWorkerUpdate,
  captureBuildBaseline, isNewBuildDeployed, isStandalone,
} from "../../utils/appUpdate";
import { useLiveSessionStore } from "../../store/liveSessionStore";

/**
 * UpdateBanner — shows a non-blocking prompt when a newer build has been
 * deployed, and applies it on the user's command. Works for both an installed
 * app ("Update now") and a browser tab ("Refresh"). Mounted once, app-wide.
 */
const UpdateBanner = () => {
  const { t } = useTranslation("common");
  const [available, setAvailable] = useState(false);
  const regRef = useRef(null);
  const standalone = isStandalone();

  // The live-broadcast control bar is fixed to the bottom-right whenever a
  // teacher is mid-lecture and has navigated away from the studio. This banner
  // is bottom-centre and, on a narrow viewport, full-width — so the two collide,
  // and the banner would cover "End broadcast".
  //
  // The banner moves, not the bar: the bar is persistent and safety-critical
  // (it is how a teacher stops broadcasting, and the proof they still are),
  // while this prompt is transient and can wait a row higher.
  const liveBarVisible = useLiveSessionStore((s) => s.isLive && !s.mountNode);

  useEffect(() => {
    let cancelled = false;

    captureBuildBaseline();
    registerServiceWorker((reg) => {
      regRef.current = reg;
      if (!cancelled) setAvailable(true);
    }).then((reg) => { if (reg) regRef.current = reg; });

    // Re-check when the user returns to the app and on a slow interval, so a
    // deploy is noticed without a manual refresh.
    const check = async () => {
      if (cancelled || available) return;
      checkServiceWorkerUpdate(regRef.current);
      if (await isNewBuildDeployed()) { if (!cancelled) setAvailable(true); }
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(check, 5 * 60 * 1000); // every 5 min

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, [available]);

  if (!available) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        // Clear the live control bar when it is showing. Two rows on a phone,
        // where that bar wraps taller; one on a wider screen.
        ...(liveBarVisible
          ? { bottom: { xs: 152, sm: 88 }, transition: "bottom .2s ease" }
          : null),
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: "center", borderRadius: 2 }}
        action={
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }}
            onClick={() => applyUpdate(regRef.current)}>
            {standalone ? t("update.update") : t("update.refresh")}
          </Button>
        }
      >
        {t("update.available")}
      </Alert>
    </Snackbar>
  );
};

export default UpdateBanner;
